import React, { useContext, useEffect, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import { createStyles, makeStyles, Theme } from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";
import { CustomDialogProps } from "../../../components/Dialog/CustomDialog";
import CustomTextField from "../../../components/Input/CustomTextField";
import { tesaBlue, tesaDarkRedError } from "../../../styles/colors";
import ProductApi from "../../../api/services/ProductApi";
import CustomDropdown from "../../../components/Input/CustomDropdown";
import UnknownProductAlert from "./UnknownProductAlert";
import useEnv from "../../../hooks/env";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import { noSeparatorDecimalCommaTransformed } from "../../../helper/YupValidationHelpers";
import { ConfirmationDialogContext } from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import { CONFIRMATION_DIALOG_NO_THOUSANDS_SEPARATOR } from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialog.configurations";
import DECIMAL_OR_THOUSANDS_SEPARATOR from "../../../constants/regex/decimal-or-thousands-separator";
import ProductEditCreateDialog from "../ProductEditCreateDialog";
import { UnknownProduct } from "../../../api/models/product/UnknownSampleAddFormType";
import ProductEditCreateButtons from "../ProductEditCreateButtons";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    invalidProdName: {
      color: tesaDarkRedError,
    },
    link: {
      cursor: "pointer",
      color: tesaBlue,
    },
    marginBottom: {
      marginBottom: theme.spacing(4),
    },
    marginBottomL: {
      marginBottom: theme.spacing(5),
    },
    marginTop: {
      marginTop: theme.spacing(5),
    },
    marginRight: {
      marginRight: theme.spacing(3),
    },
    ul: {
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(1),
      paddingLeft: theme.spacing(2),
    },
  }),
);

type UnknownProductRequest = ProductRaw.Schemas.UnknownProductRequest;
interface UnknownProductDialogData {
  name: string;
  description: string;
  color: string;
  thickness: string;
  type: "COMPETITOR" | "OTHER" | "TESA_PRODUCT" | "";
  backing: string;
  adhesive: string;
}
interface UnknownProductEditCreateDialogProps extends CustomDialogProps {
  initialData?: UnknownProduct;
  readonly?: boolean;
  exitedCallback?: () => void;
}

const UnknownProductEditCreateDialog = (props: UnknownProductEditCreateDialogProps) => {
  const classes = useStyles();
  const { exitedCallback, initialData, open, onClose, readonly } = props;
  const { api } = useEnv();
  const productApi = new ProductApi(api);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const [invalidUnknownProductName, setInvalidUnknownProductName] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const isCreate = !initialData;

  useEffect(() => {
    const interval = setTimeout(() => {
      if (searchTerm) {
        productApi
          .validateUnknownProductName(searchTerm)
          .then((res) => setInvalidUnknownProductName(res.data))
          .catch(handleHttpError);
      }
    }, 300);
    return () => {
      clearInterval(interval);
    };
  }, [searchTerm]);

  const yupValidationSchema = Yup.object().shape({
    name: Yup.string().required("Please enter a product name"),
    description: Yup.string(),
    color: Yup.string(),
    thickness: noSeparatorDecimalCommaTransformed,
    type: Yup.string().required("Please select a product type"),
    backing: Yup.string(),
    adhesive: Yup.string(),
  });

  const formToPayload = (form: UnknownProductDialogData): UnknownProductRequest => {
    return {
      ...form,
      thickness: parseFloat(form.thickness),
      // yup validation prevents empty selection ("")
      type: form.type as "COMPETITOR" | "OTHER" | "TESA_PRODUCT",
    };
  };

  const initializeForm = (productData: UnknownProduct | undefined): UnknownProductDialogData => {
    return {
      adhesive: productData?.adhesive || "",
      backing: productData?.backing || "",
      color: productData?.color || "",
      description: productData?.description || "",
      name: productData?.name || "",
      thickness: String(productData?.thickness) || "",
      type: productData?.type || "",
    };
  };

  const handleSubmit = (form: UnknownProductDialogData, productId?: number) => {
    const payload = formToPayload(form);
    const updateOrCreate = productId
      ? () => productApi.updateUnknownProduct(productId, payload)
      : () => productApi.createNewUnknownProduct(payload);
    updateOrCreate()
      .then(() => {
        setInvalidUnknownProductName(false);
        showSnackbar(`Unknown product ${productId ? "updated" : "created"}`, "success");
        onClose(true);
      })
      .catch(handleHttpError);
  };

  const confirmNoThousandsSeparator = (values: UnknownProductDialogData, regex: RegExp) => {
    return regex.test(values.thickness);
  };

  return (
    <Formik<UnknownProductDialogData>
      enableReinitialize
      initialValues={initializeForm(initialData)}
      onSubmit={(values, { setSubmitting }) => {
        if (confirmNoThousandsSeparator(values, DECIMAL_OR_THOUSANDS_SEPARATOR)) {
          showConfirmationDialog(CONFIRMATION_DIALOG_NO_THOUSANDS_SEPARATOR)
            .then(() => {
              handleSubmit(values, initialData?.id);
            })
            .catch(() => {
              setSubmitting(false);
            });
        } else {
          handleSubmit(values, initialData?.id);
        }
      }}
      validationSchema={yupValidationSchema}
    >
      {(formikProps: FormikProps<UnknownProductDialogData>): JSX.Element => {
        const { errors, handleBlur, handleChange, isSubmitting, resetForm, touched, values } = formikProps;
        return (
          <ProductEditCreateDialog
            create={isCreate}
            alert={<UnknownProductAlert className={classes.marginBottomL} />}
            open={open}
            title="unknown product"
            handleClose={onClose}
            handleExited={() => {
              resetForm();
              setInvalidUnknownProductName(false);
              exitedCallback?.();
            }}
          >
            <Form>
              <Grid container justify="flex-start" direction="row" spacing={4}>
                <Grid item xs={12}>
                  <CustomTextField
                    autoFocus
                    disabled={readonly}
                    mandatory
                    fullWidth
                    label="Product Name"
                    name="name"
                    id="name"
                    value={values.name}
                    type="text"
                    helperText={errors.name && touched.name ? errors.name : ""}
                    error={!!(errors.name && touched.name) || invalidUnknownProductName}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                  />
                  {invalidUnknownProductName && (
                    <div className={classes.invalidProdName}>
                      This product already exists in the Unknown Product Database. Please use that product or enter a
                      different name.
                    </div>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    disabled={readonly}
                    fullWidth
                    name="description"
                    id="description"
                    label="Product Description"
                    value={values.description}
                    type="text"
                    helperText={errors.description && touched.description ? errors.description : ""}
                    error={!!(errors.description && touched.description)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={3}>
                  <CustomTextField
                    disabled={readonly}
                    fullWidth
                    name="color"
                    id="color"
                    label="Color"
                    value={values.color}
                    type="text"
                    helperText={errors.color && touched.color ? errors.color : ""}
                    error={!!(errors.color && touched.color)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={3}>
                  <CustomTextField
                    disabled={readonly}
                    clamped
                    fullWidth
                    name="thickness"
                    id="thickness"
                    label="Thickness in µm"
                    value={values.thickness}
                    type="text"
                    helperText={errors.thickness && touched.thickness ? errors.thickness : ""}
                    error={!!(errors.thickness && touched.thickness)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomDropdown
                    disabled={readonly}
                    mandatory
                    labelText="Type"
                    name="type"
                    id="type"
                    value={values.type}
                    type="type"
                    error={!!(errors.type && touched.type)}
                    onChange={handleChange}
                  >
                    <MenuItem value="TESA_PRODUCT">tesa Product</MenuItem>
                    <MenuItem value="COMPETITOR">Competitor</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </CustomDropdown>
                </Grid>
                <Grid item xs={6}>
                  <CustomTextField
                    disabled={readonly}
                    fullWidth
                    name="backing"
                    id="backing"
                    label="Backing"
                    value={values.backing}
                    type="text"
                    helperText={errors.backing && touched.backing ? errors.backing : ""}
                    error={!!(errors.backing && touched.backing)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomTextField
                    disabled={readonly}
                    fullWidth
                    name="adhesive"
                    id="adhesive"
                    label="Adhesive"
                    value={values.adhesive}
                    type="text"
                    helperText={errors.adhesive && touched.adhesive ? errors.adhesive : ""}
                    error={!!(errors.adhesive && touched.adhesive)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
              </Grid>
              <ProductEditCreateButtons
                create={isCreate}
                readonly={readonly}
                disabledDismiss={isSubmitting}
                disabledConfirm={isSubmitting || invalidUnknownProductName}
                handleDismissClick={onClose}
              />
            </Form>
          </ProductEditCreateDialog>
        );
      }}
    </Formik>
  );
};

export default UnknownProductEditCreateDialog;
